import React from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {
  Box,
  Flex,
  Link,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react'

import {APP_VERSION} from '../../constants'
import {SideMenuItem} from './SideMenuItem'
import copy from './copy.json'
import {SideMenuDivider} from './SideMenuDivider'
import {CustomIcon} from '../../components'
import {useFeatureFlag} from '../../hooks/useFeatureFlag'
import {CONFIGCAT_FLAG_KEY as CAMPANION_STAFF_ACCESS_FLAG_KEY} from '../../views/CampanionStaffAccessView/constants'

interface Props {
  isOpen: boolean
  onClose: () => void
}

// Visibility is gated by two systems on purpose:
//   - The ConfigCat flag `enable-campanion-staff-access-panel` is the
//     development rollout gate. The link is hidden until we enable it.
//   - The `feature` prop on SideMenuItem hides the link from clients
//     without the `enable-campanion-staff-engagement-features` entitlement.
// The same dual check is reflected on the route in Router.tsx.
const CampanionStaffAccessMenuItem: React.FC = () => {
  const {value: enabled} = useFeatureFlag(
    CAMPANION_STAFF_ACCESS_FLAG_KEY,
    false,
  )
  if (!enabled) return null
  return (
    <SideMenuItem
      url='/campanion-staff-access'
      feature='enable-campanion-staff-engagement-features'
      icon='add-group'>
      {copy.navigationLinks.campanionStaffAccess}
    </SideMenuItem>
  )
}

export const SideMenuTemplate: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => (
  <Drawer isOpen={isOpen} placement='left' onClose={onClose}>
    <DrawerOverlay />

    <DrawerContent bg='#293135' data-testid='side-menu'>
      <DrawerHeader>
        <Flex
          flexDirection='row'
          alignItems='center'
          flex='1'
          justifyContent='space-between'>
          <CustomIcon alignSelf='center' h={['70%']} w={['70%']} name='moon' />
          <DrawerCloseButton
            color='white'
            size='lg'
            position='static'
            id='side-menu-drawer-close'
          />
        </Flex>
      </DrawerHeader>

      <DrawerBody bg='#293135' p='0'>
        <Flex flexDirection='column' alignItems='flex-start' lineHeight='24px'>
          <SideMenuItem url='/home' icon='home'>
            {copy.navigationLinks.home}
          </SideMenuItem>
          <SideMenuItem
            feature='view-photo-uploader'
            url='/albums'
            icon='camera'>
            {copy.navigationLinks.albums}
          </SideMenuItem>
          <SideMenuItem
            feature='texting-audience'
            url='/audiences'
            icon='people'>
            {copy.navigationLinks.audiences}
          </SideMenuItem>
          <SideMenuItem
            url='/microposts'
            icon='calendar' // used to be 'calendar'
          >
            {copy.navigationLinks.microposts}
          </SideMenuItem>
          <SideMenuItem
            url='/healthcheck'
            feature='covid-health-check'
            icon='mask'>
            {copy.navigationLinks.healthCheck}
          </SideMenuItem>
          <SideMenuItem
            url='/registration'
            feature='admin-menu-registration'
            icon='register'>
            {copy.navigationLinks.registation}
          </SideMenuItem>
          <CampanionStaffAccessMenuItem />
          <SideMenuDivider feature='texting-enabled'>Texting</SideMenuDivider>
          <SideMenuItem
            url='/integrations/eztexting'
            feature='texting-enabled'
            icon='texting'>
            {copy.navigationLinks.texting}
          </SideMenuItem>
          <SideMenuItem
            url='/formreminders'
            feature='forms-reminder'
            icon='reminder' // used to be 'reminder'
          >
            {copy.navigationLinks.formReminders}
          </SideMenuItem>
        </Flex>
      </DrawerBody>

      <DrawerFooter flexDirection='column' alignItems='flex-start' gap={2}>
        <Flex gap={4}>
          <Link
            as={ReactRouterLink}
            to='/privacy-policy'
            color='gray.500'
            fontSize='sm'
            fontWeight='normal'
            _hover={{color: 'white'}}>
            {copy.legalLinks.privacyPolicy}
          </Link>
          <Link
            as={ReactRouterLink}
            to='/terms-of-service'
            color='gray.500'
            fontSize='sm'
            fontWeight='normal'
            _hover={{color: 'white'}}>
            {copy.legalLinks.termsOfService}
          </Link>
        </Flex>
        <Box _hover={{color: 'gray.500'}}>{APP_VERSION}</Box>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
)
